import React, { useState, useRef } from 'react';
import { useCart } from '../context/CartContext';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { Elements, AddressElement, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../context/AuthContext';

const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!publishableKey) {
  throw new Error("REACT_APP_STRIPE_PUBLISHABLE_KEY is not set");
}

const stripePromise = loadStripe(publishableKey);

function PaymentForm({ clientSecret, email, onSuccess, onError }: {
  clientSecret: string;
  email: string;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [paymentElementReady, setPaymentElementReady] = useState(false);

  const handleConfirmPayment = async () => {
    if (!stripe || !elements) return;
    setLoading(true);
    try {
      const result = await stripe.confirmPayment({
        elements: elements || undefined,
        confirmParams: {
          receipt_email: email,
          return_url: window.location.origin + '/order-confirmation',
        },
      });
      if (result.error) {
        onError(result.error.message || 'There was a problem confirming payment.');
      } else if ('paymentIntent' in result && result.paymentIntent && (result.paymentIntent as any).status === 'succeeded') {
        onSuccess();
      }
    } catch (err) {
      onError('There was a problem confirming payment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mt-6">
        <PaymentElement onReady={() => setPaymentElementReady(true)} />
      </div>
      <button
        type="button"
        className="w-full mt-6 bg-orange text-white py-3 rounded font-bold hover:bg-logo-gray"
        disabled={loading || !paymentElementReady}
        onClick={handleConfirmPayment}
      >
        {loading ? 'Processing Payment...' : 'Pay'}
      </button>
    </>
  );
}

const Cart: React.FC = () => {
  const { items, removeItem, updateQuantity, total: subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [addressComplete, setAddressComplete] = useState(false);
  const [addressValue, setAddressValue] = useState<any>(null);
  const [addressName, setAddressName] = useState<string>('');
  const [shipping, setShipping] = useState<number | null>(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [tax, setTax] = useState<number | null>(null);

  // Helper function to round to 2 decimal places
  const roundToTwo = (num: number) => Math.round(num * 100) / 100;

  const total = roundToTwo(subtotal + (shipping || 0) + (tax || 0));

  // Calculate shipping when address is complete
  React.useEffect(() => {
    const fetchShipping = async () => {
      if (addressComplete && addressValue && items.length > 0) {
        setShippingLoading(true);
        setError(null);
        try {
          const res = await api.post('/shipping/calculate', {
            toAddress: addressValue,
            cartItems: items.map(item => ({ id: item.id, price: item.price, quantity: item.quantity })),
          });
          setShipping(Number(res.data.amount));
          // Calculate tax after shipping is determined
          setTax(roundToTwo(subtotal * 0.0825));
        } catch (err: any) {
          setError('Could not calculate shipping.');
          setShipping(null);
          setTax(null);
        } finally {
          setShippingLoading(false);
        }
      } else {
        setShipping(null);
        setTax(null);
      }
    };
    fetchShipping();
  }, [addressComplete, addressValue, items, subtotal]);

  // Step 1: Create guest order and payment intent
  const handleStartPayment = async () => {
    setError(null);
    setLoading(true);
    try {
      const orderRes = await api.post('/orders/create', {
        email,
        name: addressName,
        address: addressValue,
        items: items.map(item => ({
          puzzleId: item.id,
          quantity: item.quantity,
          ...(item.color ? { color: item.color } : {}),
          ...(item.options && item.options.length > 0 ? { options: item.options } : {}),
          ...(item.additionalRequests ? { additionalRequests: item.additionalRequests } : {}),
        })),
        shipping,
      });
      const newOrderId = orderRes.data.id;
      const piRes = await api.post('/payments/create-payment-intent', {
        orderId: newOrderId,
      });
      setClientSecret(piRes.data.clientSecret);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'There was a problem starting payment.');
    } finally {
      setLoading(false);
    }
  };

  // @ts-ignore Stripe AddressElement fields.name is valid per docs
  const addressElement = (
    <AddressElement
      options={{
        mode: 'shipping',
        fields: { name: 'always' } as any
      }}
      onChange={e => {
        setAddressComplete(e.complete);
        setAddressValue(e.value);
        setAddressName(e.value?.name || '');
      }}
    />
  );

  if (success) {
    return (
      <div className="max-w-lg mx-auto mt-12 bg-white p-8 rounded shadow text-center">
        <h2 className="text-2xl font-bold mb-4 text-green-700">Thank you!</h2>
        <p>We have received your order and will process it soon.</p>
        <Link to="/puzzles" className="text-orange font-bold">Continue Shopping</Link>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-lg mx-auto mt-12 bg-white p-8 rounded shadow text-center">
        <h2 className="text-2xl font-bold mb-4">Your Cart is Empty</h2>
        <Link to="/puzzles" className="text-orange font-bold">Browse Puzzles</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto mt-12 bg-white p-8 rounded shadow">
      <h2 className="text-2xl font-bold mb-6">Your Cart</h2>
      <div className="flex flex-col md:flex-row gap-8">
        {/* Cart Items/Details */}
        <div className="flex-1">
          <ul className="divide-y">
            {items.map(item => (
              <li key={item.id + JSON.stringify(item.options) + (item.additionalRequests || '')} className="flex justify-between items-center py-4">
                <div>
                  <div className="font-semibold">{item.title}</div>
                  <div className="text-orange font-bold">${item.price.toFixed(2)}</div>
                  {/* Show selected options if any */}
                  {item.options && item.options.length > 0 && (
                    <div className="mt-1 ml-2">
                      <div className="font-semibold text-sm mb-1">Customizations Added:</div>
                      <ul className="text-sm text-gray-700 list-disc list-inside">
                        {item.options.map((opt, idx) => (
                          <li key={opt.id || idx}>
                            {opt.name} {opt.price > 0 && (<span className="text-orange">+${opt.price.toFixed(2)}</span>)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Show additional requests if any */}
                  {item.additionalRequests && (
                    <div className="text-sm text-gray-700 mt-1 ml-2">
                      <span className="font-semibold">Additional Requests:</span> {item.additionalRequests}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    value={item.quantity}
                    onChange={e => updateQuantity(item.id, Number(e.target.value))}
                    className="w-16 border rounded px-2 py-1"
                  />
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-red-600 font-bold"
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
          {/* Clear Cart link below the cart items */}
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={clearCart}
              className="text-orange underline font-semibold hover:text-logo-gray focus:outline-none bg-transparent border-none p-0"
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            >
              Clear Cart
            </button>
          </div>
          {/* Form for email and shipping address */}
          {!clientSecret && (
            <Elements stripe={stripePromise}>
              <form ref={formRef} className="mt-8 space-y-4" onSubmit={e => e.preventDefault()}>
                <div>
                  <label className="block mb-1 font-semibold">Email</label>
                  <input
                    type="email"
                    className="w-full border px-3 py-2 rounded"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 font-semibold">Shipping Address</label>
                  {addressElement}
                </div>
              </form>
            </Elements>
          )}
          {/* Step 2: Show PaymentElement and Pay button */}
          {clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret: clientSecret as string }}>
              <PaymentForm
                clientSecret={clientSecret as string}
                email={email || ""}
                onSuccess={() => { setSuccess(true); clearCart(); }}
                onError={setError}
              />
            </Elements>
          )}
        </div>
        {/* Summary Box + Payment Button/Checkout */}
        <div className="w-full md:w-80 bg-gray-50 border border-gray-200 rounded p-6 h-fit self-start flex flex-col items-stretch">
          <h3 className="text-lg font-bold mb-4">Order Summary</h3>
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <span className="font-bold">Subtotal:</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-bold">Shipping:</span>
              <span>
                {shippingLoading ? 'Calculating...' : 
                  shipping === 0 ? 'Free' : 
                  shipping !== null ? `$${shipping.toFixed(2)}` : '--'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-bold">Estimated Tax:</span>
              <span>{shippingLoading ? 'Calculating...' : (tax !== null ? `$${tax.toFixed(2)}` : '--')}</span>
            </div>
            <div className="flex justify-between items-center text-xl mt-2">
              <span className="font-bold">Total:</span>
              <span className="font-bold text-orange">${total.toFixed(2)}</span>
            </div>
          </div>
          {/* Error message if any */}
          {error && <div className="text-red-600 text-sm mt-4">{error}</div>}
          {/* Step 1: Start payment (create order + payment intent) */}
          {!clientSecret && (
            <button
              type="button"
              className="w-full mt-6 bg-orange text-white py-3 rounded font-bold hover:bg-logo-gray"
              disabled={loading || !email || !addressComplete || shipping === null || shippingLoading}
              onClick={handleStartPayment}
            >
              {loading ? 'Loading Payment...' : 'Continue to Payment'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart; 